import { authenticatedApiService } from "../apiservice.auth";
import { TApiServiceOptions } from "../apiService";
import { API } from "@/core/apiurl";
import {
  TAnalyticsQueryRequest,
  TAnalyticsQueryResponse,
  TAnalyticsDrilldownRequest,
  TAnalyticsDrilldownResponse,
  TKpiDrilldownBody,
  TKpiDrilldownResponse,
  TAnalyticsExportRequest,
  TAnalyticsExportResponse,
  TAnalyticsMetadataResponse,
  TSavedDashboardCreatePayload,
  TSavedDashboardUpdatePayload,
  TSavedDashboardListResponse,
  TSavedDashboardMutationResponse,
  TWidgetDataRequest,
  TWidgetDataResponse,
  TWidgetCreatePayload,
  TWidgetUpdatePayload,
  TWidgetListResponse,
  TWidgetMutationResponse,
  TWidgetPosition,
  TShareDashboardPayload,
  TShareDashboardResponse,
  TDashboardSharesResponse,
  TRevokeSharePayload,
  TCustomMetricCreatePayload,
  TCustomMetricUpdatePayload,
  TCustomMetricListResponse,
  TCustomMetricMutationResponse,
  TCustomMetricPreviewPayload,
  TCustomMetricPreviewResponse,
  TCustomDimensionCreatePayload,
  TCustomDimensionUpdatePayload,
  TCustomDimensionListResponse,
  TCustomDimensionMutationResponse,
  TDerivedMetricCreatePayload,
  TDerivedMetricListResponse,
  TDerivedMetricMutationResponse,
  TEntityFieldsResponse,
  TScheduledReportCreatePayload,
  TScheduledReportUpdatePayload,
  TScheduledReportListResponse,
  TScheduledReportMutationResponse,
  TNLQueryRequest,
  TNLQueryResponse,
  TNLSuggestionsResponse,
  TAnalyticsInsightsResponse,
  TDashboardConflictStrategy,
  TDashboardImportResponse,
  TDashboardExportResponse,
} from "./analytics.types";

class AnalyticsService {
  // --- Core Query ---
  query(payload: TAnalyticsQueryRequest) {
    return authenticatedApiService.post<TAnalyticsQueryResponse>(
      API.analytics_query,
      payload,
    );
  }
  drilldown(payload: TAnalyticsDrilldownRequest) {
    return authenticatedApiService.post<TAnalyticsDrilldownResponse>(
      API.analytics_drilldown,
      payload,
    );
  }
  kpiDrilldown(payload: TKpiDrilldownBody, options?: TApiServiceOptions) {
    return authenticatedApiService.post<TKpiDrilldownResponse>(
      API.analytics_kpi_drilldown,
      payload,
      options,
    );
  }
  exportData(payload: TAnalyticsExportRequest) {
    return authenticatedApiService.post<TAnalyticsExportResponse>(
      API.analytics_export,
      payload,
    );
  }
  getMetadata() {
    return authenticatedApiService.get<TAnalyticsMetadataResponse>(
      API.analytics_metadata,
    );
  }

  // --- Fetch Dashboard Widget Data ---
  fetchDashboardWidgetData(payload: TWidgetDataRequest) {
    return authenticatedApiService.post<TWidgetDataResponse>(
      API.analytics_batch_query,
      payload,
    );
  }

  // --- Dashboard CRUD ---
  listDashboards(query = "") {
    const endpoint = query
      ? `${API.analytics_dashboards}${query}`
      : API.analytics_dashboards;
    return authenticatedApiService.get<TSavedDashboardListResponse>(endpoint);
  }
  createDashboard(payload: TSavedDashboardCreatePayload) {
    return authenticatedApiService.post<TSavedDashboardMutationResponse>(
      API.analytics_dashboards,
      payload,
    );
  }
  updateDashboard(id: string, payload: TSavedDashboardUpdatePayload) {
    return authenticatedApiService.put<TSavedDashboardMutationResponse>(
      `${API.analytics_dashboard_detail}${id}/`,
      payload,
    );
  }
  deleteDashboard(id: string) {
    return authenticatedApiService.delete<TSavedDashboardMutationResponse>(
      `${API.analytics_dashboard_detail}${id}/`,
    );
  }
  cloneDashboard(id: string) {
    return authenticatedApiService.post<TSavedDashboardMutationResponse>(
      `${API.analytics_dashboard_detail}${id}/clone/`,
      {},
    );
  }
  shareDashboard(id: string, payload: TShareDashboardPayload) {
    return authenticatedApiService.post<TShareDashboardResponse>(
      `${API.analytics_dashboard_detail}${id}/share/`,
      payload,
    );
  }
  listShares(id: string) {
    return authenticatedApiService.get<TDashboardSharesResponse>(
      `${API.analytics_dashboard_detail}${id}/share/`,
    );
  }
  revokeShare(id: string, payload: TRevokeSharePayload) {
    return authenticatedApiService.delete<{ status: boolean; message: string }>(
      `${API.analytics_dashboard_detail}${id}/share/`,
      payload,
    );
  }

  // --- Widget CRUD ---
  getDashboardWidgets(dashboardId: string) {
    return authenticatedApiService.get<TWidgetListResponse>(
      `${API.analytics_dashboard_detail}${dashboardId}/widgets/`,
    );
  }
  createWidget(dashboardId: string, payload: TWidgetCreatePayload) {
    return authenticatedApiService.post<TWidgetMutationResponse>(
      `${API.analytics_dashboard_detail}${dashboardId}/widgets/`,
      payload,
    );
  }
  updateWidget(
    dashboardId: string,
    widgetId: string,
    payload: TWidgetUpdatePayload,
  ) {
    return authenticatedApiService.put<TWidgetMutationResponse>(
      `${API.analytics_dashboard_detail}${dashboardId}/widgets/${widgetId}/`,
      payload,
    );
  }
  deleteWidget(dashboardId: string, widgetId: string) {
    return authenticatedApiService.delete<TWidgetMutationResponse>(
      `${API.analytics_dashboard_detail}${dashboardId}/widgets/${widgetId}/`,
    );
  }
  bulkUpdateWidgetPositions(
    dashboardId: string,
    positions: { widget_id: string; position: TWidgetPosition }[],
  ) {
    return authenticatedApiService.put(
      `${API.analytics_dashboard_detail}${dashboardId}/widgets/positions/`,
      { positions },
    );
  }

  // --- Admin: Custom Metrics ---
  listCustomMetrics() {
    return authenticatedApiService.get<TCustomMetricListResponse>(
      API.analytics_admin_custom_metrics,
    );
  }
  createCustomMetric(payload: TCustomMetricCreatePayload) {
    return authenticatedApiService.post<TCustomMetricMutationResponse>(
      API.analytics_admin_custom_metrics,
      payload,
    );
  }
  updateCustomMetric(id: string, payload: TCustomMetricUpdatePayload) {
    return authenticatedApiService.patch<TCustomMetricMutationResponse>(
      `${API.analytics_admin_custom_metrics}${id}/`,
      payload,
    );
  }
  deactivateCustomMetric(id: string) {
    return authenticatedApiService.delete<TCustomMetricMutationResponse>(
      `${API.analytics_admin_custom_metrics}${id}/`,
    );
  }
  previewCustomMetric(payload: TCustomMetricPreviewPayload) {
    return authenticatedApiService.post<TCustomMetricPreviewResponse>(
      API.analytics_admin_custom_metric_preview,
      payload,
    );
  }

  // --- Admin: Derived Metrics ---
  listDerivedMetrics() {
    return authenticatedApiService.get<TDerivedMetricListResponse>(
      API.analytics_admin_derived_metrics,
    );
  }
  createDerivedMetric(payload: TDerivedMetricCreatePayload) {
    return authenticatedApiService.post<TDerivedMetricMutationResponse>(
      API.analytics_admin_derived_metrics,
      payload,
    );
  }
  deactivateDerivedMetric(id: string) {
    return authenticatedApiService.delete<TDerivedMetricMutationResponse>(
      `${API.analytics_admin_derived_metrics}${id}/`,
    );
  }

  // --- Admin: Custom Dimensions ---
  listCustomDimensions() {
    return authenticatedApiService.get<TCustomDimensionListResponse>(
      API.analytics_admin_custom_dimensions,
    );
  }
  createCustomDimension(payload: TCustomDimensionCreatePayload) {
    return authenticatedApiService.post<TCustomDimensionMutationResponse>(
      API.analytics_admin_custom_dimensions,
      payload,
    );
  }
  updateCustomDimension(id: string, payload: TCustomDimensionUpdatePayload) {
    return authenticatedApiService.patch<TCustomDimensionMutationResponse>(
      `${API.analytics_admin_custom_dimensions}${id}/`,
      payload,
    );
  }
  deactivateCustomDimension(id: string) {
    return authenticatedApiService.delete<TCustomDimensionMutationResponse>(
      `${API.analytics_admin_custom_dimensions}${id}/`,
    );
  }

  // --- Admin: Entity Fields ---
  getEntityFields(entityKey: string) {
    return authenticatedApiService.get<TEntityFieldsResponse>(
      `${API.analytics_admin_entity_fields}${entityKey}/fields/`,
    );
  }

  // --- Scheduled Reports ---
  listSchedules(dashboardId: string) {
    return authenticatedApiService.get<TScheduledReportListResponse>(
      `${API.analytics_dashboard_schedules}${dashboardId}/schedules/`,
    );
  }
  createSchedule(dashboardId: string, payload: TScheduledReportCreatePayload) {
    return authenticatedApiService.post<TScheduledReportMutationResponse>(
      `${API.analytics_dashboard_schedules}${dashboardId}/schedules/`,
      payload,
    );
  }
  updateSchedule(
    dashboardId: string,
    scheduleId: string,
    payload: TScheduledReportUpdatePayload,
  ) {
    return authenticatedApiService.patch<TScheduledReportMutationResponse>(
      `${API.analytics_dashboard_schedules}${dashboardId}/schedules/${scheduleId}/`,
      payload,
    );
  }
  deleteSchedule(dashboardId: string, scheduleId: string) {
    return authenticatedApiService.delete<{ status: boolean; message: string }>(
      `${API.analytics_dashboard_schedules}${dashboardId}/schedules/${scheduleId}/`,
    );
  }
  runScheduleNow(dashboardId: string, scheduleId: string) {
    return authenticatedApiService.post<{ status: boolean; message: string }>(
      `${API.analytics_dashboard_schedules}${dashboardId}/schedules/${scheduleId}/run/`,
      {},
    );
  }

  // --- Natural Language Query ---
  nlQuery(payload: TNLQueryRequest) {
    return authenticatedApiService.post<TNLQueryResponse>(
      API.analytics_nl_query,
      payload,
    );
  }
  nlQuerySuggestions() {
    return authenticatedApiService.get<TNLSuggestionsResponse>(
      API.analytics_nl_query_suggestions,
    );
  }

  // --- Auto-Insights ---
  getInsights(days = 1) {
    return authenticatedApiService.get<TAnalyticsInsightsResponse>(
      `${API.analytics_insights}?days=${days}`,
    );
  }
  triggerInsightGeneration() {
    return authenticatedApiService.post<{ status: boolean; message: string }>(
      API.analytics_insights_generate,
      {},
    );
  }

  // --- Dashboard Import / Export ---
  importDashboard(
    file: File,
    conflictStrategy: TDashboardConflictStrategy = "skip",
    dryRun = false,
  ) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("conflict_strategy", conflictStrategy);
    formData.append("dry_run", dryRun ? "true" : "false");
    return authenticatedApiService.post<TDashboardImportResponse>(
      API.analytics_dashboard_import,
      formData,
    );
  }

  exportDashboard(dashboardId: string) {
    return authenticatedApiService.get<TDashboardExportResponse>(
      `${API.analytics_dashboard_export}${dashboardId}/export/`,
    );
  }

  downloadImportTemplate() {
    return authenticatedApiService.get<TDashboardExportResponse>(
      API.analytics_dashboard_import_template,
    );
  }
}

export const analyticsService = new AnalyticsService();
